import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

const RetornoVehiculoInstitucional = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [selectedFirma, setSelectedFirma] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [vehiculoRegistrado, setVehiculoRegistrado] = useState(null);
  const [showRetornoModal, setShowRetornoModal] = useState(false);
  const [vehiculoParaRetorno, setVehiculoParaRetorno] = useState(null);
  const [conductorNombre, setConductorNombre] = useState('');
  const [conductorApellido, setConductorApellido] = useState('');
  const [usarMismoConductor, setUsarMismoConductor] = useState(true);
  const [kmRetorno, setKmRetorno] = useState('');
  const [recorrido, setRecorrido] = useState(null);
  const [observacionesRetorno, setObservacionesRetorno] = useState('');
  const [fotoRetornoArchivo, setFotoRetornoArchivo] = useState(null);
  const [fotoRetornoPreview, setFotoRetornoPreview] = useState('');
  const [firmaRetornoArchivo, setFirmaRetornoArchivo] = useState(null);
  const [firmaRetornoPreview, setFirmaRetornoPreview] = useState('');
  const [isFirmaRetornoModalOpen, setIsFirmaRetornoModalOpen] = useState(false);
  const [isDrawingRetorno, setIsDrawingRetorno] = useState(false);
  const [firmaRetornoTieneTrazo, setFirmaRetornoTieneTrazo] = useState(false);
  const canvasRetornoRef = useRef(null);
  const capturaRetornoInputId = useMemo(() => `retorno-vehiculo-foto-captura-${uuidv4()}`, []);
  const [fieldErrorsRetorno, setFieldErrorsRetorno] = useState({});

  const apiOrigin = useMemo(() => {
    try {
      return new URL(environment.apiUrl).origin;
    } catch (error) {
      console.warn('No se pudo obtener origin de apiUrl:', error);
      if (typeof window !== 'undefined') {
        return `${window.location.protocol}//${window.location.host}`;
      }
      return '';
    }
  }, []);

  const resolveFotoUrl = useCallback(
    (fotoPath) => {
      if (!fotoPath) return null;
      if (/^https?:\/\//i.test(fotoPath)) return fotoPath;
      if (!apiOrigin) return fotoPath;
      if (fotoPath.startsWith('/')) {
        return `${apiOrigin}${fotoPath}`;
      }
      return `${apiOrigin}/${fotoPath}`;
    },
    [apiOrigin]
  );

  const hasPuestoAsignado = useMemo(() => Boolean(user?.id_generado), [user]);

  const fetchStatus = useCallback(
    async (showLoader = true) => {
      if (!user) return;

      if (!hasPuestoAsignado) {
        setStatus(null);
        setVehiculos([]);
        setError('Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.');
        setLoading(false);
        return;
      }

      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError('');
      setSuccess('');

      try {
        const statusUrl = new URL(getApiUrl('BITACORA', 'STATUS'));
        statusUrl.searchParams.set('idGenerado', user.id_generado);
        statusUrl.searchParams.set('username', user.username || '');
        statusUrl.searchParams.set('appid', user.appid || '');

        const [statusResponse, vehiculosResponse] = await Promise.all([
          fetch(statusUrl.toString()),
          (async () => {
            const vehiculosUrl = new URL(getApiUrl('BITACORA', 'LISTAR_VEHICULOS_INSTITUCIONALES_SALIDA'));
            vehiculosUrl.searchParams.set('idGenerado', user.id_generado);
            vehiculosUrl.searchParams.set('username', user.username || '');
            vehiculosUrl.searchParams.set('appid', user.appid || '');
            return fetch(vehiculosUrl.toString());
          })()
        ]);

        const statusData = await statusResponse.json();
        const vehiculosData = await vehiculosResponse.json();

        if (statusResponse.ok && statusData.success) {
          setStatus(statusData.data);
        } else {
          setStatus(prevStatus => prevStatus || null);
          setError(statusData.error || 'No fue posible obtener el estado de la bitácora.');
        }

        if (vehiculosResponse.ok && vehiculosData.success) {
          setVehiculos(Array.isArray(vehiculosData.data) ? vehiculosData.data : []);
        } else {
          setVehiculos([]);
          setError((prev) => prev || vehiculosData.error || 'No fue posible listar los vehículos institucionales con salida registrada.');
        }
      } catch (err) {
        console.error('Error cargando vehículos institucionales con salida:', err);
        setStatus(prevStatus => prevStatus || null);
        setVehiculos([]);
        setError('Error de conexión al cargar la información de vehículos institucionales.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user, hasPuestoAsignado]
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRegistrarRetorno = (vehiculoId) => {
    if (!status || !vehiculoId) return;

    // Encontrar el vehículo
    const vehiculo = vehiculos.find(v => v.id === vehiculoId);
    if (!vehiculo) return;

    // Configurar el vehículo y abrir el modal
    setVehiculoParaRetorno(vehiculo);
    setUsarMismoConductor(true);
    
    // Extraer nombre y apellido del conductor original
    const nombreCompleto = vehiculo.p_nombre || '';
    const partes = nombreCompleto.trim().split(/\s+/);
    const nombreOriginal = partes[0] || '';
    const apellidoOriginal = partes.slice(1).join(' ') || '';
    
    setConductorNombre(nombreOriginal);
    setConductorApellido(apellidoOriginal);
    setKmRetorno('');
    setObservacionesRetorno('');
    setFotoRetornoArchivo(null);
    setFotoRetornoPreview('');
    setFirmaRetornoArchivo(null);
    setFirmaRetornoPreview('');
    setFirmaRetornoTieneTrazo(false);
    setIsDrawingRetorno(false);
    setFieldErrorsRetorno({});
    setError('');
    setShowRetornoModal(true);
  };

  const handleCerrarRetornoModal = useCallback(() => {
    setShowRetornoModal(false);
    setVehiculoParaRetorno(null);
    setConductorNombre('');
    setConductorApellido('');
    setUsarMismoConductor(true);
    setKmRetorno('');
    setRecorrido(null);
    setObservacionesRetorno('');
    setFotoRetornoArchivo(null);
    setFotoRetornoPreview('');
    setFirmaRetornoArchivo(null);
    setFirmaRetornoPreview('');
    setIsFirmaRetornoModalOpen(false);
    setFirmaRetornoTieneTrazo(false);
    setIsDrawingRetorno(false);
    setFieldErrorsRetorno({});
    setSubmittingId(null);
  }, []);

  // Calcular recorrido automáticamente cuando cambie el KM de retorno
  useEffect(() => {
    if (showRetornoModal && vehiculoParaRetorno && kmRetorno) {
      const kmSalida = vehiculoParaRetorno.km;
      const kmRetornoValue = parseFloat(kmRetorno);
      
      if (kmSalida !== null && !isNaN(kmSalida) && !isNaN(kmRetornoValue) && kmRetornoValue >= kmSalida) {
        const diferencia = kmRetornoValue - kmSalida;
        setRecorrido(diferencia);
      } else if (kmRetornoValue < kmSalida && !isNaN(kmRetornoValue)) {
        setRecorrido(null);
      } else {
        setRecorrido(null);
      }
    } else if (!kmRetorno) {
      setRecorrido(null);
    }
  }, [kmRetorno, vehiculoParaRetorno, showRetornoModal]);

  const handleConfirmarRetorno = useCallback(async () => {
    if (!status || !vehiculoParaRetorno) return;

    // Validar todos los campos
    const errors = {};
    let firstErrorField = null;

    const nombreFinal = usarMismoConductor ? conductorNombre : conductorNombre.trim();
    const apellidoFinal = usarMismoConductor ? conductorApellido : conductorApellido.trim();

    if (!nombreFinal) {
      errors.conductorNombre = 'El nombre del conductor es obligatorio';
      if (!firstErrorField) firstErrorField = 'retorno-conductor-nombre';
    }

    if (!apellidoFinal) {
      errors.conductorApellido = 'El apellido del conductor es obligatorio';
      if (!firstErrorField) firstErrorField = 'retorno-conductor-apellido';
    }

    if (!kmRetorno || kmRetorno.toString().trim() === '' || isNaN(parseFloat(kmRetorno)) || parseFloat(kmRetorno) < 0) {
      errors.kmRetorno = 'El kilometraje es obligatorio y debe ser un número válido mayor o igual a 0';
      if (!firstErrorField) firstErrorField = 'retorno-km';
    }

    if (!observacionesRetorno || !observacionesRetorno.trim()) {
      errors.observacionesRetorno = 'Las observaciones son obligatorias';
      if (!firstErrorField) firstErrorField = 'retorno-observaciones';
    }

    if (!fotoRetornoArchivo) {
      errors.fotoRetorno = 'La evidencia fotográfica es obligatoria';
      if (!firstErrorField) firstErrorField = 'retorno-foto';
    }

    if (!firmaRetornoArchivo) {
      errors.firmaRetorno = 'La firma del conductor es obligatoria';
      if (!firstErrorField) firstErrorField = 'retorno-firma';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrorsRetorno(errors);
      if (firstErrorField) {
        setTimeout(() => scrollToFieldRetorno(firstErrorField), 100);
      }
      return;
    }

    setSubmittingId(vehiculoParaRetorno.id);
    setError('');
    setFieldErrorsRetorno({});

    try {
      const formData = new FormData();
      formData.append('idGenerado', status.idGenerado);
      formData.append('registroId', vehiculoParaRetorno.id.toString());
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      formData.append('conductorNombre', nombreFinal);
      formData.append('conductorApellido', apellidoFinal);
      formData.append('km', kmRetorno.trim());
      formData.append('observaciones', observacionesRetorno.trim());
      if (recorrido !== null && !isNaN(recorrido)) {
        formData.append('recorrido', recorrido.toString());
      }
      
      if (fotoRetornoArchivo) {
        formData.append('foto', fotoRetornoArchivo);
      }
      if (firmaRetornoArchivo) {
        formData.append('firma', firmaRetornoArchivo);
      }

      const response = await fetch(getApiUrl('BITACORA', 'REGISTRAR_RETORNO_VEHICULO_INST'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const successMessage = data.message || 'Retorno de vehículo institucional registrado correctamente.';
        setSuccess(successMessage);
        setVehiculoRegistrado(vehiculoParaRetorno);
        setShowSuccessModal(true);
        handleCerrarRetornoModal();
        // Actualizar la lista
        await fetchStatus(false);
      } else {
        setError(data.error || 'No fue posible registrar el retorno del vehículo institucional.');
      }
    } catch (err) {
      console.error('Error registrando retorno de vehículo institucional:', err);
      setError('Error de conexión al registrar el retorno.');
    } finally {
      setSubmittingId(null);
    }
  }, [status, vehiculoParaRetorno, usarMismoConductor, conductorNombre, conductorApellido, kmRetorno, observacionesRetorno, fotoRetornoArchivo, firmaRetornoArchivo, recorrido, user, handleCerrarRetornoModal, fetchStatus]);

  const scrollToFieldRetorno = (fieldId) => {
    const field = document.getElementById(fieldId);
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus();
      field.classList.add('ring-2', 'ring-red-500');
      setTimeout(() => {
        field.classList.remove('ring-2', 'ring-red-500');
      }, 2000);
    }
  };

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    setSuccess('');
    setVehiculoRegistrado(null);
    // Actualizar la lista después de cerrar el modal
    setTimeout(() => {
      if (user) {
        fetchStatus(false).catch(err => {
          console.error('Error actualizando lista:', err);
        });
      }
    }, 200);
  }, [user, fetchStatus]);

  const handleOpenFoto = (vehiculo) => {
    if (!vehiculo?.foto_url) return;
    setSelectedVehiculo(vehiculo);
  };

  const handleCloseFoto = () => {
    setSelectedVehiculo(null);
  };

  const handleOpenFirma = (vehiculo) => {
    if (!vehiculo?.foto_firma_url) return;
    setSelectedFirma(vehiculo);
  };

  const handleCloseFirma = () => {
    setSelectedFirma(null);
  };

  // Funciones para foto y firma del modal de retorno
  const optimizeImageRetorno = useCallback((file, { maxWidth = 1024, quality = 0.7 } = {}) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      reader.onerror = reject;

      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No se pudo optimizar la imagen.'));
              return;
            }
            const baseName = file.name.replace(/\.[^/.]+$/, '');
            const optimizedFile = new File([blob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            resolve(optimizedFile);
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileChangeRetorno = useCallback(
    async (event) => {
      const file = event.target.files?.[0] || null;

      if (!file) {
        setFotoRetornoArchivo(null);
        setFotoRetornoPreview('');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten imágenes para la evidencia fotográfica.');
        event.target.value = '';
        return;
      }

      let finalFile = file;
      const ONE_MB = 1024 * 1024;

      try {
        if (file.size > ONE_MB) {
          const optimized = await optimizeImageRetorno(file, { maxWidth: 1024, quality: 0.7 });
          finalFile = optimized;
        }
      } catch (optimizationError) {
        console.error('Error optimizando imagen:', optimizationError);
        setError('No se pudo optimizar la imagen. Intenta con otra foto.');
        event.target.value = '';
        return;
      }

      setFotoRetornoArchivo(finalFile);
      setFotoRetornoPreview(URL.createObjectURL(finalFile));
      setFieldErrorsRetorno((prev) => {
        if (prev.fotoRetorno) {
          const next = { ...prev };
          delete next.fotoRetorno;
          return next;
        }
        return prev;
      });
    },
    [optimizeImageRetorno]
  );

  const handleOpenFirmaRetornoModal = useCallback(() => {
    setError('');
    setIsFirmaRetornoModalOpen(true);
  }, []);

  const handleCloseFirmaRetornoModal = useCallback(() => {
    setIsFirmaRetornoModalOpen(false);
    setIsDrawingRetorno(false);
    setFirmaRetornoTieneTrazo(false);
  }, []);

  const getCanvasRetornoCoordinates = useCallback((event) => {
    const canvas = canvasRetornoRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX ?? 0) - rect.left) * (canvas.width / rect.width);
    const y = ((event.clientY ?? 0) - rect.top) * (canvas.height / rect.height);
    return { x, y };
  }, []);

  const handleFirmaRetornoPointerDown = useCallback((event) => {
    if (!isFirmaRetornoModalOpen || !canvasRetornoRef.current) return;
    event.preventDefault();
    const ctx = canvasRetornoRef.current.getContext('2d');
    const { x, y } = getCanvasRetornoCoordinates(event);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawingRetorno(true);
    setFirmaRetornoTieneTrazo(true);
  }, [getCanvasRetornoCoordinates, isFirmaRetornoModalOpen]);

  const handleFirmaRetornoPointerMove = useCallback((event) => {
    if (!isDrawingRetorno || !canvasRetornoRef.current) return;
    event.preventDefault();
    const ctx = canvasRetornoRef.current.getContext('2d');
    const { x, y } = getCanvasRetornoCoordinates(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [getCanvasRetornoCoordinates, isDrawingRetorno]);

  const handleFirmaRetornoPointerUp = useCallback((event) => {
    if (!canvasRetornoRef.current) return;
    event?.preventDefault();
    const ctx = canvasRetornoRef.current.getContext('2d');
    ctx.closePath();
    setIsDrawingRetorno(false);
  }, []);

  const handleClearFirmaRetorno = useCallback(() => {
    if (!canvasRetornoRef.current) return;
    const ctx = canvasRetornoRef.current.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRetornoRef.current.width, canvasRetornoRef.current.height);
    setFirmaRetornoTieneTrazo(false);
    setIsDrawingRetorno(false);
    setError('');
  }, []);

  const handleGuardarFirmaRetorno = useCallback(() => {
    if (!canvasRetornoRef.current) return;
    if (!firmaRetornoTieneTrazo) {
      setError('Por favor registra la firma del conductor.');
      return;
    }
    setError('');
    canvasRetornoRef.current.toBlob((blob) => {
      if (!blob) {
        setError('No se pudo generar la firma. Intenta nuevamente.');
        return;
      }
      const file = new File([blob], `firma_retorno_${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      if (firmaRetornoPreview) {
        URL.revokeObjectURL(firmaRetornoPreview);
      }
      const previewUrl = URL.createObjectURL(blob);
      setFirmaRetornoArchivo(file);
      setFirmaRetornoPreview(previewUrl);
      setIsFirmaRetornoModalOpen(false);
      setFieldErrorsRetorno((prev) => {
        const next = { ...prev };
        delete next.firmaRetorno;
        return next;
      });
    }, 'image/jpeg', 0.9);
  }, [firmaRetornoPreview, firmaRetornoTieneTrazo]);

  const handleEliminarFirmaRetorno = useCallback(() => {
    if (firmaRetornoPreview) {
      URL.revokeObjectURL(firmaRetornoPreview);
    }
    setFirmaRetornoArchivo(null);
    setFirmaRetornoPreview('');
    setFirmaRetornoTieneTrazo(false);
    setIsDrawingRetorno(false);
    setError('');
  }, [firmaRetornoPreview]);

  // useEffect para limpiar previews
  useEffect(() => {
    if (!fotoRetornoPreview) return undefined;
    return () => {
      URL.revokeObjectURL(fotoRetornoPreview);
    };
  }, [fotoRetornoPreview]);

  useEffect(() => {
    if (!firmaRetornoPreview) return undefined;
    return () => {
      URL.revokeObjectURL(firmaRetornoPreview);
    };
  }, [firmaRetornoPreview]);

  useEffect(() => {
    if (!isFirmaRetornoModalOpen) {
      return;
    }
    const canvas = canvasRetornoRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setFirmaRetornoTieneTrazo(false);
    setIsDrawingRetorno(false);
  }, [isFirmaRetornoModalOpen]);

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Retorno de vehículo institucional</h1>
            <p className="text-gray-600">
              Consulta los vehículos institucionales que salieron hoy y registra su retorno.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchStatus(false)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
            disabled={loading || refreshing || submittingId !== null}
          >
            {refreshing ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Actualizando...</span>
              </span>
            ) : (
              'Actualizar lista'
            )}
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">Cargando vehículos institucionales con salida registrada…</span>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl shadow">
              <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Vehículos institucionales en salida</h2>
                  <p className="text-sm text-gray-500">
                    Vehículos institucionales que salieron hoy y aún no han retornado.
                  </p>
                </div>
                <span className={`text-sm font-semibold ${vehiculos.length === 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Total: <span className="font-semibold">{vehiculos.length}</span>
                </span>
              </div>

              {vehiculos.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">
                  No hay vehículos institucionales pendientes de retorno hoy.
                </div>
              ) : (
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vehiculos.map((vehiculo) => (
                    <div
                      key={vehiculo.id}
                      className="border border-gray-200 rounded-xl p-4 bg-gray-50 flex flex-col space-y-3 shadow-sm relative"
                    >
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenFoto(vehiculo)}
                          disabled={!vehiculo.foto_url}
                          className="p-2 rounded-full border border-transparent text-green-600 hover:text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          title={vehiculo.foto_url ? 'Ver evidencia fotográfica' : 'Sin evidencia fotográfica'}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenFirma(vehiculo)}
                          disabled={!vehiculo.foto_firma_url}
                          className="p-2 rounded-full border border-transparent text-orange-500 hover:text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          title={vehiculo.foto_firma_url ? 'Ver firma' : 'Sin firma registrada'}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 18l6-6 4 4 8-8"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14 7l7-3-3 7"
                            />
                          </svg>
                        </button>
                      </div>

                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Vehículo</p>
                        <h3 className="text-base font-semibold text-indigo-600 break-words">
                          {vehiculo.v_placa || 'Sin placa'}
                        </h3>
                      </div>

                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Conductor</p>
                        <p className="text-sm font-medium text-gray-700 break-words">
                          {vehiculo.p_nombre || 'Sin nombre'}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Ingreso</p>
                          <p className="font-medium">{vehiculo.hora_ingreso || '—'}</p>
                        </div>
                        <div className="flex-1 min-w-[10rem]">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Observación</p>
                          <p className="font-medium text-gray-700 break-words">
                            {vehiculo.observacion || '—'}
                          </p>
                        </div>
                      </div>

                      {vehiculo.km && (
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Kilometraje</p>
                          <p className="text-sm font-medium text-gray-700">{vehiculo.km}</p>
                        </div>
                      )}

                      {vehiculo.v_autorizacion && (
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">N. Autorización</p>
                          <p className="text-sm font-medium text-gray-700 break-words">{vehiculo.v_autorizacion}</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex-1">
                          <button
                            type="button"
                            onClick={() => handleRegistrarRetorno(vehiculo.id)}
                            className="inline-flex items-center justify-center w-full px-3 py-2 border border-transparent text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                            disabled={submittingId === vehiculo.id}
                          >
                            {submittingId === vehiculo.id ? 'Registrando…' : 'Registrar retorno'}
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-dashed border-gray-300 text-xs text-gray-500">
                        {vehiculo.foto_url ? 'Toca el ícono verde para ver la evidencia fotográfica' : 'Sin evidencia fotográfica cargada'}
                        <br />
                        {vehiculo.foto_firma_url ? 'Usa el ícono naranja para ver la firma.' : 'Sin firma registrada.'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {selectedVehiculo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Evidencia Fotográfica</h3>
                <p className="text-sm text-gray-500">{selectedVehiculo.p_nombre} - {selectedVehiculo.v_placa}</p>
              </div>
              <button
                type="button"
                onClick={handleCloseFoto}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gray-900">
              <img
                src={resolveFotoUrl(selectedVehiculo.foto_url)}
                alt={`Evidencia fotográfica de ${selectedVehiculo.p_nombre} - ${selectedVehiculo.v_placa}`}
                className="w-full h-auto mx-auto object-contain max-h-[70vh]"
              />
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-right">
              <button
                type="button"
                onClick={handleCloseFoto}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedFirma && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Firma del conductor</h3>
                <p className="text-sm text-gray-500">{selectedFirma.p_nombre} - {selectedFirma.v_placa}</p>
              </div>
              <button
                type="button"
                onClick={handleCloseFirma}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gray-900">
              <img
                src={resolveFotoUrl(selectedFirma.foto_firma_url)}
                alt={`Firma de ${selectedFirma.p_nombre} - ${selectedFirma.v_placa}`}
                className="w-full h-auto mx-auto object-contain max-h-[70vh]"
              />
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-right">
              <button
                type="button"
                onClick={handleCloseFirma}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de retorno de vehículo */}
      {showRetornoModal && vehiculoParaRetorno && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 overflow-y-auto" 
          style={{ zIndex: 9998 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && submittingId !== vehiculoParaRetorno.id) {
              handleCerrarRetornoModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Registrar Retorno</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Vehículo: <span className="font-semibold">{vehiculoParaRetorno.v_placa}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCerrarRetornoModal}
                  disabled={submittingId === vehiculoParaRetorno.id}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-2 disabled:opacity-50"
                  aria-label="Cerrar"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenido del formulario */}
            <div className="px-6 py-5 space-y-5 max-h-[calc(100vh-300px)] overflow-y-auto">
              {/* Selección de conductor */}
              <div>
                <div className="flex items-center justify-between mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Usar mismo conductor que salió
                    </p>
                    <p className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-700">{vehiculoParaRetorno.p_nombre}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const nuevoValor = !usarMismoConductor;
                      setUsarMismoConductor(nuevoValor);
                      if (nuevoValor) {
                        // Activar: restaurar nombre y apellido del conductor original
                        const nombreCompleto = vehiculoParaRetorno.p_nombre || '';
                        const partes = nombreCompleto.trim().split(/\s+/);
                        setConductorNombre(partes[0] || '');
                        setConductorApellido(partes.slice(1).join(' ') || '');
                        // Limpiar errores relacionados
                        setFieldErrorsRetorno((prev) => {
                          const next = { ...prev };
                          delete next.conductorNombre;
                          delete next.conductorApellido;
                          return next;
                        });
                      } else {
                        // Desactivar: limpiar campos para que ingresen nuevo conductor
                        setConductorNombre('');
                        setConductorApellido('');
                        // Limpiar errores relacionados
                        setFieldErrorsRetorno((prev) => {
                          const next = { ...prev };
                          delete next.conductorNombre;
                          delete next.conductorApellido;
                          return next;
                        });
                      }
                    }}
                    disabled={submittingId === vehiculoParaRetorno.id}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      usarMismoConductor ? 'bg-green-500' : 'bg-gray-300'
                    } ${submittingId === vehiculoParaRetorno.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={usarMismoConductor}
                    aria-label="Usar mismo conductor que salió"
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        usarMismoConductor ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {!usarMismoConductor && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label htmlFor="retorno-conductor-nombre" className="block text-sm font-medium mb-1" style={{ color: '#0066ff' }}>
                        Nombre del conductor <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="retorno-conductor-nombre"
                        type="text"
                        value={conductorNombre}
                        onChange={(e) => {
                          setConductorNombre(e.target.value);
                          setFieldErrorsRetorno((prev) => {
                            if (prev.conductorNombre) {
                              const next = { ...prev };
                              delete next.conductorNombre;
                              return next;
                            }
                            return prev;
                          });
                        }}
                        disabled={submittingId === vehiculoParaRetorno.id}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                          fieldErrorsRetorno.conductorNombre ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {fieldErrorsRetorno.conductorNombre && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrorsRetorno.conductorNombre}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="retorno-conductor-apellido" className="block text-sm font-medium mb-1" style={{ color: '#0066ff' }}>
                        Apellido del conductor <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="retorno-conductor-apellido"
                        type="text"
                        value={conductorApellido}
                        onChange={(e) => {
                          setConductorApellido(e.target.value);
                          setFieldErrorsRetorno((prev) => {
                            if (prev.conductorApellido) {
                              const next = { ...prev };
                              delete next.conductorApellido;
                              return next;
                            }
                            return prev;
                          });
                        }}
                        disabled={submittingId === vehiculoParaRetorno.id}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                          fieldErrorsRetorno.conductorApellido ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {fieldErrorsRetorno.conductorApellido && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrorsRetorno.conductorApellido}</p>
                      )}
                    </div>
                  </div>
                )}

                {usarMismoConductor && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Conductor:</span> {vehiculoParaRetorno.p_nombre}
                    </p>
                  </div>
                )}
              </div>

              {/* Kilometraje */}
              <div>
                <label htmlFor="retorno-km" className="block text-sm font-medium text-gray-700 mb-1">
                  Kilometraje al retornar (KM) <span className="text-red-500">*</span>
                </label>
                <input
                  id="retorno-km"
                  type="number"
                  min="0"
                  step="0.1"
                  value={kmRetorno}
                  onChange={(e) => {
                    setKmRetorno(e.target.value);
                    setFieldErrorsRetorno((prev) => {
                      if (prev.kmRetorno) {
                        const next = { ...prev };
                        delete next.kmRetorno;
                        return next;
                      }
                      return prev;
                    });
                  }}
                  onBlur={(e) => {
                    // Validar que el KM de retorno sea mayor o igual al de salida
                    if (vehiculoParaRetorno && e.target.value) {
                      const kmSalida = vehiculoParaRetorno.km;
                      const kmRetornoValue = parseFloat(e.target.value);
                      
                      if (kmSalida !== null && !isNaN(kmSalida) && !isNaN(kmRetornoValue) && kmRetornoValue < kmSalida) {
                        setFieldErrorsRetorno((prev) => ({
                          ...prev,
                          kmRetorno: 'El kilometraje de retorno suele ser mayor o igual al de salida'
                        }));
                      }
                    }
                  }}
                  disabled={submittingId === vehiculoParaRetorno.id}
                  placeholder="Ej: 12500"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    (() => {
                      if (fieldErrorsRetorno.kmRetorno) {
                        return 'border-red-500';
                      }
                      if (kmRetorno && vehiculoParaRetorno && !isNaN(parseFloat(kmRetorno))) {
                        const kmRetornoValue = parseFloat(kmRetorno);
                        const kmSalida = vehiculoParaRetorno.km;
                        if (kmSalida !== null && !isNaN(kmSalida) && kmRetornoValue < kmSalida) {
                          return 'border-yellow-500 bg-yellow-50';
                        }
                      }
                      return 'border-gray-300';
                    })()
                  }`}
                />
                {vehiculoParaRetorno?.km !== null && vehiculoParaRetorno?.km !== undefined && (
                  <p className={`mt-1 text-xs ${(() => {
                    if (kmRetorno && !isNaN(parseFloat(kmRetorno))) {
                      const kmRetornoValue = parseFloat(kmRetorno);
                      const kmSalida = vehiculoParaRetorno.km;
                      if (kmRetornoValue < kmSalida) {
                        return 'text-orange-700 font-semibold';
                      }
                    }
                    return 'text-gray-500';
                  })()}`}>
                    KM de salida: <span className="font-semibold">{vehiculoParaRetorno.km}</span>
                    {kmRetorno && !isNaN(parseFloat(kmRetorno)) && parseFloat(kmRetorno) < vehiculoParaRetorno.km && (
                      <span className="ml-1">(Alerta)</span>
                    )}
                  </p>
                )}
                {recorrido !== null && !isNaN(recorrido) && (
                  <p className="mt-1 text-sm font-semibold text-green-600">
                    Recorrido: <span>{recorrido.toFixed(1)} km</span>
                  </p>
                )}
                {fieldErrorsRetorno.kmRetorno && (
                  <p className="mt-1 text-sm text-gray-600">{fieldErrorsRetorno.kmRetorno}</p>
                )}
              </div>

              {/* Observaciones */}
              <div>
                <label htmlFor="retorno-observaciones" className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="retorno-observaciones"
                  value={observacionesRetorno}
                  onChange={(e) => {
                    setObservacionesRetorno(e.target.value);
                    setFieldErrorsRetorno((prev) => {
                      if (prev.observacionesRetorno) {
                        const next = { ...prev };
                        delete next.observacionesRetorno;
                        return next;
                      }
                      return prev;
                    });
                  }}
                  disabled={submittingId === vehiculoParaRetorno.id}
                  rows={3}
                  placeholder="Ej: Vehículo rayado/golpeado, conductor en estado etílico, vidrios rotos..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrorsRetorno.observacionesRetorno ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {fieldErrorsRetorno.observacionesRetorno && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrorsRetorno.observacionesRetorno}</p>
                )}
              </div>

              {/* Evidencia fotográfica */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Evidencia Fotográfica <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {!fotoRetornoPreview ? (
                    <div>
                      <input
                        id={capturaRetornoInputId}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileChangeRetorno}
                        disabled={submittingId === vehiculoParaRetorno.id}
                        className="hidden"
                      />
                      <label
                        htmlFor={capturaRetornoInputId}
                        className={`inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                          fieldErrorsRetorno.fotoRetorno ? 'ring-2 ring-red-500' : ''
                        }`}
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Capturar foto
                      </label>
                      {fieldErrorsRetorno.fotoRetorno && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrorsRetorno.fotoRetorno}</p>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={fotoRetornoPreview}
                        alt="Vista previa de la foto"
                        className="w-full max-h-64 object-contain rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFotoRetornoArchivo(null);
                          setFotoRetornoPreview('');
                          if (fotoRetornoPreview) {
                            URL.revokeObjectURL(fotoRetornoPreview);
                          }
                        }}
                        disabled={submittingId === vehiculoParaRetorno.id}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                        aria-label="Eliminar foto"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Firma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Firma del conductor <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {!firmaRetornoPreview ? (
                    <div>
                      <button
                        type="button"
                        onClick={handleOpenFirmaRetornoModal}
                        disabled={submittingId === vehiculoParaRetorno.id}
                        className={`inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          fieldErrorsRetorno.firmaRetorno ? 'ring-2 ring-red-500' : ''
                        }`}
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Capturar firma
                      </button>
                      {fieldErrorsRetorno.firmaRetorno && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrorsRetorno.firmaRetorno}</p>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={firmaRetornoPreview}
                        alt="Vista previa de la firma"
                        className="w-full max-h-32 object-contain rounded-lg border border-gray-300 bg-white"
                      />
                      <button
                        type="button"
                        onClick={handleEliminarFirmaRetorno}
                        disabled={submittingId === vehiculoParaRetorno.id}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                        aria-label="Eliminar firma"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Error general */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}
            </div>

            {/* Footer con botones */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleCerrarRetornoModal}
                disabled={submittingId === vehiculoParaRetorno.id}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmarRetorno}
                disabled={submittingId === vehiculoParaRetorno.id}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingId === vehiculoParaRetorno.id ? 'Registrando...' : 'Registrar retorno'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de firma para retorno */}
      {isFirmaRetornoModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseFirmaRetornoModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Capturar firma del conductor</h3>
              <button
                type="button"
                onClick={handleCloseFirmaRetornoModal}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded-full p-1"
                aria-label="Cerrar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <canvas
                ref={canvasRetornoRef}
                width={800}
                height={300}
                onPointerDown={handleFirmaRetornoPointerDown}
                onPointerMove={handleFirmaRetornoPointerMove}
                onPointerUp={handleFirmaRetornoPointerUp}
                onPointerLeave={handleFirmaRetornoPointerUp}
                className="w-full border-2 border-gray-300 rounded-lg cursor-crosshair touch-none"
                style={{ touchAction: 'none' }}
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <button
                type="button"
                onClick={handleClearFirmaRetorno}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                Limpiar
              </button>
              <div className="space-x-3">
                <button
                  type="button"
                  onClick={handleCloseFirmaRetornoModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleGuardarFirmaRetorno}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  Guardar firma
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de éxito para retorno registrado */}
      {showSuccessModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseSuccessModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con icono de éxito */}
            <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Retorno registrado</h3>
                  <p className="text-sm text-gray-600 mt-0.5">Proceso completado exitosamente</p>
                </div>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="px-6 py-5 space-y-4">
              {/* Información del vehículo */}
              {vehiculoRegistrado && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-semibold text-gray-700">Placa:</span>
                      <span className="text-sm font-bold text-indigo-600">{vehiculoRegistrado.v_placa || '—'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-600">{vehiculoRegistrado.p_nombre || '—'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Mensaje de confirmación */}
              <div className="text-center">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {success || 'El retorno del vehículo institucional ha sido registrado correctamente en la bitácora.'}
                </p>
              </div>

              {/* Indicador visual */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500">Registro confirmado</span>
              </div>
            </div>

            {/* Footer con botón */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseSuccessModal}
                className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default RetornoVehiculoInstitucional;

